import { useState, useEffect } from 'react';
import { Loader2, Plus, ChevronLeft, ChevronRight, Disc3, Calendar, AlertCircle, X, Check, Pencil, LayoutGrid, List } from 'lucide-react';
import { listVaults, listMemberships, createVault, pressVault } from '../services/api';
import { useAuthStore } from '../store/authStore';
import ArtistDashboardNav from '../components/dashboard/ArtistDashboardNav';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';
import { buildTenantPath } from '../hooks/useTenant';
import { useViewMode } from '../hooks/useViewMode';

interface Vault {
  id: number;
  tenantId: number;
  name: string | null;
  status: string;
  artworkValidationStatus: 'PENDING' | 'VERIFIED' | 'REJECTED' | 'ERROR';
  artworkValidationReason: string | null;
  artworkValidatedAt: number | null;
  issueDate: number | null;
  coverS3Uri: string | null;
  coverUrl: string | null;
  information: string | null;
  productionType: string | null;
  productionLimit: number | null;
  productionExpiryDate: number | null;
  memberships: string | null;
  keysIssued: number;
  createdAt: number;
  updatedAt: number;
}

interface Membership {
  id: number;
  name: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function PressingsPage({ slug }: { slug: string }) {
  const { token } = useAuthStore();
  const onboardingProgress = useOnboardingProgress(slug);
  const [vaults, setVaults] = useState<Vault[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useViewMode('pressings_view', 'cards');
  const [pressModal, setPressModal] = useState<{ vault: Vault } | null>(null);
  const [pressingDate, setPressingDate] = useState('');
  const [sendReleaseTransmission, setSendReleaseTransmission] = useState(false);
  const [pressing, setPressing] = useState(false);
  const [pressError, setPressError] = useState<string | null>(null);

  const fetchVaults = async (page = 1) => {
    try {
      const res = await listVaults(slug, { page, limit: 20 }, token);
      if (res.ok) {
        const json = await res.json() as { data: Vault[]; pagination: PaginationData };
        setVaults(json.data);
        setPagination(json.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch vaults:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberships = async () => {
    try {
      const res = await listMemberships(slug);
      if (res.ok) {
        const json = await res.json() as { data: Membership[] };
        setMemberships(json.data);
      }
    } catch {}
  };

  useEffect(() => {
    fetchVaults();
    fetchMemberships();
  }, [slug]);

  const getMembershipNames = (membershipsJson: string | null): string => {
    if (!membershipsJson) return '—';
    try {
      const ids: number[] = JSON.parse(membershipsJson);
      if (!Array.isArray(ids) || ids.length === 0) return '—';
      return ids.map(id => {
        const m = memberships.find(mem => mem.id === id);
        return m ? m.name : `#${id}`;
      }).join(', ');
    } catch {
      return '—';
    }
  };

  const formatDate = (ts: number | null): string => {
    if (!ts) return '—';
    return new Date(ts * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const hasMembershipSelection = (membershipsJson: string | null): boolean => {
    if (!membershipsJson) return false;
    try {
      const ids: unknown = JSON.parse(membershipsJson);
      return Array.isArray(ids) && ids.some((id) => Number.isFinite(Number(id)));
    } catch {
      return false;
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      SCHEDULED: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      PRESSED: 'bg-green-500/20 text-green-400 border-green-500/30',
      OUT_OF_PRODUCTION: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border ${colors[status] || colors.DRAFT}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  const getArtworkValidationBadge = (validationStatus: Vault['artworkValidationStatus']) => {
    const colors: Record<string, string> = {
      VERIFIED: 'bg-green-500/20 text-green-400 border-green-500/30',
      PENDING: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      REJECTED: 'bg-red-500/20 text-red-400 border-red-500/30',
      ERROR: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono border ${colors[validationStatus] || colors.PENDING}`}>
        ARTWORK: {validationStatus}
      </span>
    );
  };

  const handleCreateVault = async () => {
    try {
      const res = await createVault(slug, {}, token);
      if (res.ok) {
        const json = await res.json() as { data: Vault };
        window.location.href = buildTenantPath(slug, `pressings/${json.data.id}`);
      }
    } catch (err) {
      console.error('Failed to create vault:', err);
    }
  };

  const openPressModal = (vault: Vault) => {
    const today = new Date().toISOString().split('T')[0];
    setPressingDate(today);
    setSendReleaseTransmission(false);
    setPressError(null);
    setPressModal({ vault });
  };

  const handlePress = async () => {
    if (!pressModal) return;
    setPressing(true);
    setPressError(null);

    const selectedTs = Math.floor(new Date(pressingDate).getTime() / 1000);

    try {
      const res = await pressVault(
        slug,
        pressModal.vault.id,
        { pressingDate: selectedTs, sendReleaseTransmission },
        token
      );

      if (res.ok) {
        setPressModal(null);
        fetchVaults(pagination.page);
        onboardingProgress.refresh();
      } else {
        const errJson = await res.json() as { error: string; details?: string[] };
        setPressError(errJson.details ? errJson.details.join(', ') : errJson.error);
      }
    } catch (err) {
      setPressError('Failed to schedule pressing');
    } finally {
      setPressing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <ArtistDashboardNav slug={slug} currentPage="pressings" onboardingProgress={onboardingProgress} />

      <main className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Pressings</h1>
            <p className="text-sm text-text-secondary mt-1 font-mono">Create and manage your music pressings</p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-white/5 border border-white/10 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-1.5 rounded transition-colors cursor-pointer ${viewMode === 'cards' ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-text-muted hover:text-white'}`}
                title="Card view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded transition-colors cursor-pointer ${viewMode === 'table' ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-text-muted hover:text-white'}`}
                title="Table view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleCreateVault}
              className="btn-primary text-sm !px-4 !py-2 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Pressing
            </button>
          </div>
        </div>

        {/* Content */}
        {vaults.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Disc3 className="w-12 h-12 text-electric-cyan/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No pressings yet</h3>
            <p className="text-text-secondary text-sm mb-6">Create your first pressing and give your fans something to collect.</p>
            <button
              onClick={handleCreateVault}
              className="btn-primary text-sm !px-4 !py-2 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Pressing
            </button>
          </div>
        ) : viewMode === 'cards' ? (
          /* Card View */
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {vaults.map((v) => (
                (() => {
                  const canSchedule = (v.artworkValidationStatus || 'PENDING') === 'VERIFIED' && hasMembershipSelection(v.memberships);
                  const scheduleTitle = !hasMembershipSelection(v.memberships)
                    ? 'Add at least one membership before scheduling'
                    : (v.artworkValidationStatus || 'PENDING') === 'VERIFIED'
                      ? 'Schedule pressing'
                      : 'Artwork must be VERIFIED before pressing';

                  return (
                <div
                  key={v.id}
                  className="glass-card-hover rounded-xl overflow-hidden flex flex-col"
                >
                  {/* Cover art */}
                  <div className="relative aspect-square bg-white/5">
                    {v.coverUrl ? (
                      <img
                        src={v.coverUrl}
                        alt={v.name || 'Pressing cover'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Disc3 className="w-12 h-12 text-text-muted/30" />
                      </div>
                    )}
                    {/* Status badge overlay */}
                    <div className="absolute top-2 left-2">
                      {getStatusBadge(v.status)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-semibold text-white text-sm truncate mb-1">
                      {v.name || <span className="text-text-muted italic">Untitled</span>}
                    </h3>
                    <div className="mb-2">
                      {getArtworkValidationBadge(v.artworkValidationStatus || 'PENDING')}
                    </div>
                    <p className="font-mono text-xs text-text-secondary mb-1 truncate">
                      {v.productionType || 'No type'}
                    </p>
                    <p className="font-mono text-xs text-text-muted mb-3">
                      {formatDate(v.issueDate)}
                    </p>
                    <div className="mt-auto pt-3 border-t border-white/5 flex items-center justify-between">
                      <span className="font-mono text-xs text-text-muted">
                        {(v.status === 'PRESSED' || v.status === 'OUT_OF_PRODUCTION')
                          ? <span className="text-electric-cyan">{v.keysIssued} issued</span>
                          : getMembershipNames(v.memberships)}
                      </span>
                      <div className="flex items-center gap-1">
                        {v.status === 'DRAFT' && (
                          <button
                            onClick={() => { window.location.href = buildTenantPath(slug, `pressings/${v.id}`); }}
                            className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors cursor-pointer"
                            title="Edit pressing"
                          >
                            <Pencil className="w-3.5 h-3.5 text-text-muted hover:text-electric-cyan" />
                          </button>
                        )}
                        {v.status === 'DRAFT' && (
                          <button
                            onClick={() => openPressModal(v)}
                            disabled={!canSchedule}
                            className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors cursor-pointer"
                            title={scheduleTitle}
                          >
                            <Calendar className={`w-3.5 h-3.5 ${canSchedule ? 'text-text-muted hover:text-electric-cyan' : 'text-text-muted/40'}`} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                  );
                })()
              ))}
            </div>

            {/* Pagination for cards */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <span className="text-xs text-text-secondary font-mono">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchVaults(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="p-1.5 rounded border border-white/10 hover:border-electric-cyan/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => fetchVaults(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="p-1.5 rounded border border-white/10 hover:border-electric-cyan/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Table View */
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary w-12"></th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Status</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Name</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Created</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Issue Date</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Type</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Memberships</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Issued</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vaults.map((v) => (
                    (() => {
                      const canSchedule = (v.artworkValidationStatus || 'PENDING') === 'VERIFIED' && hasMembershipSelection(v.memberships);
                      const scheduleTitle = !hasMembershipSelection(v.memberships)
                        ? 'Add at least one membership before scheduling'
                        : (v.artworkValidationStatus || 'PENDING') === 'VERIFIED'
                          ? 'Schedule pressing'
                          : 'Artwork must be VERIFIED before pressing';

                      return (
                    <tr
                      key={v.id}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <td className="px-4 py-3">
                        {v.coverUrl ? (
                          <img
                            src={v.coverUrl}
                            alt={v.name || 'Pressing cover'}
                            className="w-10 h-10 min-w-[2.5rem] min-h-[2.5rem] aspect-square rounded object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 min-w-[2.5rem] min-h-[2.5rem] aspect-square rounded bg-white/5 flex items-center justify-center">
                            <Disc3 className="w-4 h-4 text-text-muted" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(v.status)}
                        {v.status === 'DRAFT' && (
                          <div className="mt-1">
                            {getArtworkValidationBadge(v.artworkValidationStatus || 'PENDING')}
                          </div>
                        )}
                      </td>
                      <td
                        className="px-4 py-3 font-medium text-white cursor-pointer"
                        onClick={() => {
                          if (v.status === 'DRAFT') {
                            window.location.href = buildTenantPath(slug, `pressings/${v.id}`);
                          }
                        }}
                      >
                        {v.name || <span className="text-text-muted italic">Untitled</span>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-text-secondary whitespace-nowrap">
                        {formatDate(v.createdAt)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-text-secondary whitespace-nowrap">
                        {formatDate(v.issueDate)}
                      </td>
                      <td className="px-4 py-3 text-xs text-text-secondary">
                        {v.productionType || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-text-secondary max-w-[200px] truncate">
                        {getMembershipNames(v.memberships)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-text-secondary">
                        {(v.status === 'PRESSED' || v.status === 'OUT_OF_PRODUCTION') ? (
                          <span className="text-electric-cyan">{v.keysIssued}</span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {v.status === 'DRAFT' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); window.location.href = buildTenantPath(slug, `pressings/${v.id}`); }}
                              title="Edit pressing"
                              className="p-1.5 rounded hover:bg-white/10 text-text-secondary hover:text-electric-cyan transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {v.status === 'DRAFT' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openPressModal(v); }}
                              title={scheduleTitle}
                              disabled={!canSchedule}
                              className="p-1.5 rounded hover:bg-white/10 text-text-secondary hover:text-electric-cyan transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Calendar className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                      );
                    })()
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
                <span className="text-xs text-text-secondary font-mono">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchVaults(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="p-1.5 rounded border border-white/10 hover:border-electric-cyan/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => fetchVaults(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="p-1.5 rounded border border-white/10 hover:border-electric-cyan/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Schedule Pressing Modal */}
      {pressModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass-card p-6 w-full max-w-md mx-4 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Schedule Pressing</h3>
              <button onClick={() => setPressModal(null)} className="text-text-muted hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-text-secondary mb-4">
              Choose a pressing date for <strong className="text-white">{pressModal.vault.name || 'Untitled'}</strong>.
              Selecting today will immediately press this pressing.
            </p>

            <div className="mb-4 rounded border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              This is a one-way operation. Once scheduled or pressed, this pressing can no longer be edited.
            </div>

            {pressError && (
              <div className="flex items-start gap-2 p-3 rounded bg-red-500/10 border border-red-500/30 mb-4">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-400">{pressError}</p>
              </div>
            )}

            <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-2">
              Pressing Date
            </label>
            <input
              type="date"
              value={pressingDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setPressingDate(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-electric-cyan/50 focus:outline-none mb-4"
            />

            <label className="mb-4 flex items-start gap-3 rounded border border-white/10 bg-white/5 px-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sendReleaseTransmission}
                onChange={(e) => setSendReleaseTransmission(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-transparent text-electric-cyan focus:ring-electric-cyan/40"
              />
              <span className="text-sm text-white">Send a release transmission to eligable memberships</span>
            </label>

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setPressModal(null)}
                className="px-4 py-2 text-sm text-text-secondary hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePress}
                disabled={pressing || !pressingDate}
                className="btn-primary text-sm !px-4 !py-2 inline-flex items-center gap-2 disabled:opacity-50"
              >
                {pressing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {pressingDate === new Date().toISOString().split('T')[0] ? 'Press Now' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
